/**
 * TEXT / MTEXT serializers (§3.2 decision 6). `text` exports as single-line
 * TEXT; `mtext` exports as native MTEXT with `\n` → `\P` and the classic
 * 250-byte chunk split (repeating code 3 + final code 1) at code-point
 * boundaries so multi-byte characters never tear.
 */

import type { Entity } from '../../../../state/store';
import type { DxfWriteContext, TextStyleTableEntry } from '../types';
import type { DxfStreamWriter } from '../DxfStreamWriter';
import { entityPrologue } from './common';
import { renderFieldText, hasFieldCodes } from '../../../../core/commands/fields';

/**
 * FIELD export substitution (Faz 8.8): AutoCAD carries no yap field cache,
 * so the RESOLVED value exports into the text (OCS writes values into the
 * host too). Unresolved codes export with their %<…>% markup.
 */
function exportFieldContent(raw: string, entityId: string): string {
    return hasFieldCodes(raw) ? renderFieldText(raw, entityId) : raw;
}

/** UTF-8 byte length of a string (chunking measures bytes, like OCS). */
function utf8Len(s: string): number {
    let n = 0;
    for (const ch of s) {
        const cp = ch.codePointAt(0) ?? 0;
        n += cp < 0x80 ? 1 : cp < 0x800 ? 2 : cp < 0x10000 ? 3 : 4;
    }
    return n;
}

/** Split into ≤maxByte pieces at code-point boundaries. */
function splitChunks(text: string, maxBytes = 250): string[] {
    const out: string[] = [];
    let cur = '';
    let curLen = 0;
    for (const ch of text) {
        const cp = ch.codePointAt(0) ?? 0;
        const bytes = cp < 0x80 ? 1 : cp < 0x800 ? 2 : cp < 0x10000 ? 3 : 4;
        if (cur && curLen + bytes > maxBytes) {
            out.push(cur);
            cur = '';
            curLen = 0;
        }
        cur += ch;
        curLen += bytes;
    }
    if (cur || out.length === 0) out.push(cur);
    return out;
}

/** Resolve the DXF STYLE record for an entity's styleId + its height. */
export function resolveTextStyle(ctx: DxfWriteContext, styleId: unknown): { entry: TextStyleTableEntry } {
    const key = typeof styleId === 'string' ? styleId : '';
    const entry = ctx.styleEntries.get(key) ?? ctx.styleEntries.get('STANDARD')!;
    return { entry };
}

export function writeText(w: DxfStreamWriter, ctx: DxfWriteContext, e: Entity, owner: string): void {
    const d = e.data as Record<string, unknown>;
    const p = d.position as number[];
    if (!Array.isArray(p)) return;
    const { entry } = resolveTextStyle(ctx, d.styleId);
    const content = exportFieldContent(String(d.content ?? ''), e.id).replace(/\r?\n/g, ' ');
    const height = typeof d.height === 'number' && d.height > 0 ? d.height : entry.height;
    entityPrologue(w, ctx, e, 'TEXT', owner);
    w.pair(100, 'AcDbText');
    w.pair(10, p[0] ?? 0).pair(20, p[1] ?? 0).pair(30, p[2] ?? 0);
    w.pair(40, height);
    w.pair(1, content);
    const rot = typeof d.rotation === 'number' ? d.rotation : 0;
    if (rot) w.pair(50, ((rot % 360) + 360) % 360);
    if (d.halign || d.valign) {
        w.pair(72, typeof d.halign === 'number' ? d.halign : 0);
        w.pair(11, p[0] ?? 0).pair(21, p[1] ?? 0).pair(31, p[2] ?? 0);
        w.pair(73, typeof d.valign === 'number' ? d.valign : 0);
    } else {
        w.pair(11, p[0] ?? 0).pair(21, p[1] ?? 0).pair(31, p[2] ?? 0);
    }
    w.pair(7, entry.name);
    w.pair(100, 'AcDbText');
}

export function writeMText(w: DxfStreamWriter, ctx: DxfWriteContext, e: Entity, owner: string): void {
    const d = e.data as Record<string, unknown>;
    const p = d.position as number[];
    if (!Array.isArray(p)) return;
    const { entry } = resolveTextStyle(ctx, d.styleId);
    const height = typeof d.height === 'number' && d.height > 0 ? d.height : entry.height;
    // Format codes in the stored content are preserved verbatim; only new
    // lines become MTEXT paragraph breaks. FIELD codes substitute their
    // resolved cache value on export (Faz 8.8).
    const body = exportFieldContent(String(d.content ?? ''), e.id).replace(/\r\n|\r|\n/g, '\\P');
    const chunks = splitChunks(body);
    entityPrologue(w, ctx, e, 'MTEXT', owner);
    w.pair(100, 'AcDbMText');
    w.pair(10, p[0] ?? 0).pair(20, p[1] ?? 0).pair(30, p[2] ?? 0);
    w.pair(40, height);
    w.pair(41, typeof d.width === 'number' && d.width > 0 ? d.width : 0);
    // Attachment point from the style alignment (1 topLeft / 2 topCenter / 3 topRight).
    const entityAlign = typeof d.alignment === 'string' ? d.alignment : '';
    const align = (entityAlign || entry.alignment) as 'left' | 'center' | 'right';
    w.pair(71, align === 'center' ? 2 : align === 'right' ? 3 : 1);
    w.pair(72, 1); // drawing direction: horizontal
    // Chunks: repeating code 3, final code 1 (empty body still needs code 1).
    chunks.forEach((chunk, i) => {
        if (i < chunks.length - 1) w.pair(3, chunk);
        else w.pair(1, chunk);
    });
    w.pair(7, entry.name);
    w.pair(73, 1); // line spacing style: at least
    w.pair(44, 1.0);
    w.pair(11, 1).pair(21, 0).pair(31, 0);
    // R2018 column blocks (101 "Embedded Object") are only written when
    // columns exist — none in Faz 1 (gate: ctx.version.mText2018Columns).
}
